"use client"

import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { motion } from "framer-motion"
import { ArrowRight, BookOpen, Target, TrendingUp, Clock } from "lucide-react"
import { useUser } from "@stackframe/stack"
import { Suspense, useEffect, useState } from "react"

// 骨架屏组件
const HeroSkeleton = () => (
  <section className="relative w-full h-[85vh] min-h-[600px] flex items-center justify-center overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-slate-900 to-purple-900 z-0" />
    <div className="relative z-10 container mx-auto px-4 text-center text-white">
      <div className="h-16 bg-white/10 rounded-lg animate-pulse mb-4" />
      <div className="h-8 bg-white/10 rounded-lg animate-pulse mb-8 max-w-2xl mx-auto" />
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <div className="h-12 w-32 bg-white/10 rounded-lg animate-pulse" />
        <div className="h-12 w-32 bg-white/10 rounded-lg animate-pulse" />
      </div>
    </div>
  </section>
)

// 未登录用户的Hero内容
const GuestHeroContent = () => (
  <>
    <motion.h1
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeInOut" }}
      className="text-4xl md:text-6xl font-extrabold tracking-tight"
    >
      掌握软考高级认证，开启职业新高度
    </motion.h1>

    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2, ease: "easeInOut" }}
      className="mt-4 max-w-2xl mx-auto text-lg md:text-xl text-slate-300"
    >
      专业的计算机软件资格考试学习平台，汇聚优质资源，智能测试系统，助您一次通关。
    </motion.p>

    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.4, ease: "easeInOut" }}
      className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4"
    >
      <Button
        size="lg"
        asChild
        className="bg-orange-500 hover:bg-orange-600 text-white"
      >
        <Link href="/handler/signup">
          立即注册开始学习 <ArrowRight className="ml-2 h-5 w-5" />
        </Link>
      </Button>
      <Button
        size="lg"
        variant="outline"
        className="border-white/60 text-white bg-white/10 hover:bg-white hover:text-slate-900 backdrop-blur-sm transition-all duration-300"
        asChild
      >
        <Link href="#subjects">浏览科目</Link>
      </Button>
    </motion.div>
  </>
)

// 已登录但未选择科目用户的Hero内容
const NoSubjectHeroContent = ({ userName }: { userName: string }) => (
  <>
    <motion.h1
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeInOut" }}
      className="text-4xl md:text-6xl font-extrabold tracking-tight"
    >
      欢迎回来，{userName}！
    </motion.h1>

    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2, ease: "easeInOut" }}
      className="mt-4 max-w-2xl mx-auto text-lg md:text-xl text-slate-300"
    >
      选择您的目标科目，开始系统化的学习之旅。我们为您准备了完整的学习资源和智能练习系统。
    </motion.p>

    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.4, ease: "easeInOut" }}
      className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4"
    >
      <Button
        size="lg"
        asChild
        className="bg-orange-500 hover:bg-orange-600 text-white"
      >
        <Link href="/subject-selection">
          选择学习科目 <ArrowRight className="ml-2 h-5 w-5" />
        </Link>
      </Button>
      <Button
        size="lg"
        variant="outline"
        className="border-white/60 text-white bg-white/10 hover:bg-white hover:text-slate-900 backdrop-blur-sm transition-all duration-300"
        asChild
      >
        <Link href="/questions">开始练习</Link>
      </Button>
    </motion.div>
  </>
)

// 已选择科目用户的Hero内容
const WithSubjectHeroContent = ({ userName, subjectName }: { userName: string, subjectName: string }) => (
  <>
    <motion.h1
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeInOut" }}
      className="text-4xl md:text-6xl font-extrabold tracking-tight"
    >
      继续您的{subjectName}学习
    </motion.h1>

    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2, ease: "easeInOut" }}
      className="mt-4 max-w-2xl mx-auto text-lg md:text-xl text-slate-300"
    >
      {userName}，您的学习进度正在稳步提升。继续保持学习节奏，距离通过考试更近一步！
    </motion.p>

    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.4, ease: "easeInOut" }}
      className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto"
    >
      <Card className="bg-white/10 border-white/20 text-white">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm">
            <BookOpen className="h-4 w-4" />
            今日练习
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline" className="w-full border-white/60 text-white bg-white/10 hover:bg-white hover:text-slate-900 backdrop-blur-sm transition-all duration-300">
            <Link href="/questions/daily">开始练习</Link>
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-white/10 border-white/20 text-white">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Target className="h-4 w-4" />
            学习计划
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline" className="w-full border-white/60 text-white bg-white/10 hover:bg-white hover:text-slate-900 backdrop-blur-sm transition-all duration-300">
            <Link href="/study-plan">查看计划</Link>
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-white/10 border-white/20 text-white">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm">
            <TrendingUp className="h-4 w-4" />
            学习统计
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline" className="w-full border-white/60 text-white bg-white/10 hover:bg-white hover:text-slate-900 backdrop-blur-sm transition-all duration-300">
            <Link href="/profile">查看进度</Link>
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  </>
)

// 主Hero组件内容
const HeroContent = () => {
  const user = useUser()
  const [userSubject, setUserSubject] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchUserSubject = async () => {
      if (user) {
        try {
          // 动态导入用户科目相关的action
          const { getUserPrimarySubjectAction } = await import("@/lib/actions/user-subject-actions")
          const response = await getUserPrimarySubjectAction()

          if (response.success && response.data) {
            setUserSubject(response.data.name)
          } else {
            setUserSubject(null)
          }
          setLoading(false)
        } catch (error) {
          console.error('获取用户科目失败:', error)
          setUserSubject(null)
          setLoading(false)
        }
      } else {
        setLoading(false)
      }
    }

    fetchUserSubject()
  }, [user])

  if (loading) {
    return <HeroSkeleton />
  }

  const userName = user?.displayName || user?.primaryEmail?.split('@')[0] || '同学'

  // 根据用户状态渲染不同内容
  if (!user) {
    return <GuestHeroContent />
  } else if (!userSubject) {
    return <NoSubjectHeroContent userName={userName} />
  } else {
    return <WithSubjectHeroContent userName={userName} subjectName={userSubject} />
  }
}

export const SmartHeroSection = () => {
  return (
    <section className="relative w-full h-[85vh] min-h-[600px] flex items-center justify-center overflow-hidden">
      {/* Background Gradient & Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-slate-900 to-purple-900 z-0">
        {/* 添加一些装饰性的背景元素 */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-orange-500/20 rounded-full blur-xl" />
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-blue-500/20 rounded-full blur-xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 container mx-auto px-4 text-center text-white">
        <Suspense fallback={<HeroSkeleton />}>
          <HeroContent />
        </Suspense>
      </div>
    </section>
  )
}
